// https://github.com/angus-c/just/tree/d8c5dd18941062d8db7e9310ecc8f53fd607df54/packages/function-debounce
type Debounced<A extends unknown[], R> = {
  (this: unknown, ...args: A): R | undefined;
  cancel: () => void;
  flush: () => void;
};

function debounce<A extends unknown[], R>(
  fn: (this: unknown, ...args: A) => R,
  wait: number,
  callFirst?: boolean,
): Debounced<A, R> {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  let debouncedFn: (() => void) | null = null;

  const clear = function () {
    if (timeout) {
      clearTimeout(timeout);

      debouncedFn = null;
      timeout = null;
    }
  };

  const flush = function () {
    const call = debouncedFn;

    clear();

    if (call) {
      return call();
    }
  };

  const debounceWrapper = function (this: unknown, ...args: A): R | undefined {
    if (!wait) {
      return fn.apply(this, args);
    }

    const callNow = callFirst && !timeout;

    clear();

    debouncedFn = () => {
      fn.apply(this, args);
    };

    timeout = setTimeout(function () {
      timeout = null;

      if (!callNow) {
        const call = debouncedFn;

        debouncedFn = null;

        call?.();
      }
    }, wait);

    if (callNow) {
      debouncedFn();
    }
    return undefined;
  };

  debounceWrapper.cancel = clear;
  debounceWrapper.flush = flush;

  return debounceWrapper;
}

export { debounce as default };
